import React, { useEffect, useState } from "react";
import { Pencil, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Small coloured tag shown next to an altered item / customer name.
export function AlteredLabel({ by, testid }) {
  if (!by) return null;
  const isAdmin = by === "admin";
  return (
    <span
      data-testid={testid}
      className={`ml-1.5 shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border font-bold ${
        isAdmin ? "border-amber-400/60 bg-amber-400/10 text-amber-300" : "border-purple-400/60 bg-purple-400/10 text-purple-300"
      }`}
    >
      {isAdmin ? "admin altered" : "co-ad altered"}
    </span>
  );
}

// The struck-through previous fabric/colour shown above an altered item row,
// tagged REJECTED exactly like a rejected row.
export function AlteredOriginalLine({ item, testid }) {
  if (!item || !item.altered_by) return null;
  return (
    <span className="flex items-center gap-1.5 text-[11px] text-white/40 line-through leading-tight" data-testid={testid}>
      {item.original_fabric} <span className="text-white/30 no-underline">·</span> {item.original_color}
      <span className="text-red-400 no-underline text-[9px] uppercase tracking-wider">REJECTED</span>
    </span>
  );
}

// Tag shown on a deleted item row (admin del / co-ad del).
export function DeletedLabel({ by, testid }) {
  if (!by) return null;
  return (
    <span data-testid={testid} className="ml-1.5 shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border font-bold border-red-500/60 bg-red-500/15 text-red-300">
      {by === "admin" ? "admin del" : "co-ad del"}
    </span>
  );
}

// Struck-through previous customer name shown next to the active name on the order header.
export function AlteredCustomerName({ order, testidPrefix }) {
  if (!order || !order.client_altered_by) return null;
  return (
    <>
      <span className="text-white/40 line-through mr-1" data-testid={`${testidPrefix}-original`}>{order.original_client_name}</span>
      <AlteredLabel by={order.client_altered_by} testid={`${testidPrefix}-label`} />
    </>
  );
}

// Pencil button — only mounted for admin / co-admin callers.
export function EditPencil({ onClick, testid, title = "Edit" }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title} className="shrink-0 p-1 text-white/40 hover:text-white transition-colors">
      <Pencil className="h-3.5 w-3.5" />
    </button>
  );
}

// Dialog to pick a new fabric + colour via a single search over real stock combos.
// `stockOptions` is the live stock list ([{fabric,color,...}]); only existing
// fabric+colour combinations are selectable, so the backend can never reject it.
export function AlterItemDialog({ open, onOpenChange, stockOptions, item, onSave }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null); // {fabric,color}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) { setQuery(""); setPicked(null); setSaving(false); setError(""); }
  }, [open, item]);

  const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
  const combos = React.useMemo(() => {
    const seen = new Set(); const out = [];
    (stockOptions || []).forEach((s) => {
      if (!s || !s.fabric || !s.color) return;
      const k = `${s.fabric}|||${s.color}`;
      if (seen.has(k)) return; seen.add(k);
      out.push({ fabric: s.fabric, color: s.color, blob: norm(s.fabric) + norm(s.color) });
    });
    return out.sort((a, b) => a.fabric.localeCompare(b.fabric) || a.color.localeCompare(b.color));
  }, [stockOptions]);

  const tokens = query.trim().toLowerCase().split(/\s+/).map((t) => t.replace(/\s/g, "")).filter(Boolean);
  const results = combos.filter((c) => tokens.every((t) => c.blob.includes(t))).slice(0, 80);

  const save = async () => {
    if (!picked) return;
    setSaving(true); setError("");
    try {
      await onSave(picked.fabric, picked.color);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (e?.message || "Could not save the alteration. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md rounded-none" data-testid="alter-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl tracking-tight text-white">Alter item</DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Search an existing fabric + colour (e.g. "vardhman black"). Ordered quantity stays unchanged; the previous item is kept struck-through.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <Input autoFocus value={query} onChange={(e) => { setQuery(e.target.value); setPicked(null); }} placeholder="Type fabric and/or colour…" data-testid="alter-item-search"
              className="rounded-none h-10 bg-[#141414] border-white/10 pl-10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
          </div>
          <div className="border border-white/10 bg-[#0A0A0A] divide-y divide-white/5 max-h-60 overflow-auto" data-testid="alter-item-results">
            {results.length === 0 && <div className="px-3 py-3 text-xs text-white/40">No matching fabric + colour in stock.</div>}
            {results.map((c) => {
              const sel = picked && picked.fabric === c.fabric && picked.color === c.color;
              return (
                <button key={`${c.fabric}|${c.color}`} onClick={() => setPicked({ fabric: c.fabric, color: c.color })}
                  data-testid={`alter-item-option-${slugify(c.fabric)}-${slugify(c.color)}`}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${sel ? "bg-white text-black" : "text-white hover:bg-white/5"}`}>
                  {c.fabric} <span className={sel ? "text-black/50" : "text-white/40"}>·</span> {c.color}
                </button>
              );
            })}
          </div>
          {error && <p className="text-xs text-red-400 border border-red-500/40 bg-red-500/10 px-3 py-2" data-testid="alter-item-error">{error}</p>}
          <Button onClick={save} disabled={saving || !picked} data-testid="alter-item-save" className="w-full rounded-none h-10 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider disabled:opacity-40">
            {saving ? "Saving…" : picked ? `Save — ${picked.fabric} · ${picked.color}` : "Select a fabric + colour"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Dialog to pick an existing customer (search-driven). searchCustomers(q) -> [{id,name,...}]
export function CustomerPickerDialog({ open, onOpenChange, searchCustomers, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setQ(""); setResults([]); } }, [open]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try { const r = await searchCustomers(q); if (active) setResults(r || []); }
      catch { if (active) setResults([]); }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, open, searchCustomers]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-md rounded-none" data-testid="alter-customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl tracking-tight text-white">Change customer</DialogTitle>
          <DialogDescription className="text-white/50 text-xs">Pick an existing customer. The previous name stays struck-through on the order; no customer record is modified.</DialogDescription>
        </DialogHeader>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers by name…" data-testid="alter-customer-search" className="rounded-none h-10 bg-[#141414] border-white/10 pl-10 text-white text-sm" />
        </div>
        <div className="border border-white/10 bg-[#0A0A0A] divide-y divide-white/5 max-h-72 overflow-auto" data-testid="alter-customer-results">
          {loading && <div className="px-3 py-3 text-xs text-white/40">Searching…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-3 text-xs text-white/40">No customers found.</div>}
          {results.map((c) => (
            <button key={c.id} onClick={() => { onPick(c); onOpenChange(false); }} data-testid={`alter-customer-option-${c.id}`} className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors">
              <span className="text-sm text-white">{c.name}</span>
              {c.place ? <span className="text-white/40 text-xs ml-2">{c.place}</span> : null}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
